import React from 'react';
import { BodyText, Button, BoldBodyText, FlexRow, Grid, TinyBodyText } from 'ui-toolkit';
import { Form, Formik } from 'formik';
import { IOrderOption } from '../../types/accounting/IOrderOption';
import styled from 'styled-components';
import { IConversationOrderData } from '../../types/conversations/IConversation';
import { OrderSelectField } from '../shared/OrderSelect/OrderSelectField';
import { eConversationChannelType } from '../../types/conversations/eConversationChannelType';
import { editConversationSchema } from './validation/editConversationSchema';
import { AutoSubmit } from '../shared/AutoSubmit';
import { ContactSelectField } from './ContactSelectField';
import { ContactOptionsType } from '../../types/conversations/IConversationContact';

const SaveButtonContainer = styled.div`
  text-align: right;
`;

const RoomyGrid = styled(Grid)`
  min-width: 300px;
  gap: ${(props) => props.theme.spacing.spacing5};
`;

export interface IEditConversationFormProps {
  conversationValue: string;
  conversationType: eConversationChannelType;
  contacts: ContactOptionsType;
  conversationOrder: IConversationOrderData;
  disabled: boolean;
  onSubmit: (values: IConversationOrderData) => void;
  onOrderSelect: (option: IOrderOption | undefined) => void;
  saveButtonText: string;
  selectedOrder?: IOrderOption;
  onCancel?: () => void;
  autoSave?: boolean;
}

const OrderHintText: React.FC<{ option: IOrderOption }> = ({ option }) => {
  const address = option.data.propertyAddress;

  return (
    <div>
      <BoldBodyText>Address</BoldBodyText>
      <BodyText>{address.streetAddress}</BodyText>
      <BodyText>
        {address.city} {address.stateAbbreviation}, {address.zipCode}
      </BodyText>
      <FlexRow>
        <BoldBodyText>Loan Number</BoldBodyText>
        <BodyText>{option.data.loanNumber}</BodyText>
      </FlexRow>
    </div>
  );
};

export const EditConversationForm: React.FC<IEditConversationFormProps> = ({
  conversationValue,
  conversationType,
  contacts,
  conversationOrder,
  disabled,
  onSubmit,
  onOrderSelect,
  saveButtonText,
  selectedOrder,
  onCancel,
  autoSave,
}) => {
  return (
    <Formik
      enableReinitialize
      initialValues={conversationOrder}
      onSubmit={(values) => onSubmit(values)}
      validationSchema={editConversationSchema}
    >
      {({ isValid, values, handleSubmit }) => (
        <Form>
          <RoomyGrid columnCount={1}>
            <OrderSelectField
              name="orderId"
              inputId="orderId"
              label="File Number"
              isRequired
              placeholder="Enter File Number"
              isClearable
              hintText={selectedOrder && <OrderHintText option={selectedOrder} />}
              onOptionSet={onOrderSelect}
            />
            <TinyBodyText>
              {conversationType === eConversationChannelType.Phone && 'Phone Number'}
              {conversationType === eConversationChannelType.Email && 'Email Address'}
              <BodyText>{conversationValue}</BodyText>
            </TinyBodyText>
            <ContactSelectField
              name="contact"
              isClearable
              label="Contact"
              menuPlacement="auto"
              menuPosition="fixed"
              options={contacts}
              placeholder="Select Contact"
            />
            {autoSave ? (
              <AutoSubmit isValid={isValid} values={values} submitForm={handleSubmit} />
            ) : (
              <SaveButtonContainer>
                {onCancel && (
                  <Button intent="secondary" onClick={onCancel}>
                    Cancel
                  </Button>
                )}
                <Button intent={'primary'} type="submit" disabled={disabled || !isValid}>
                  {saveButtonText}
                </Button>
              </SaveButtonContainer>
            )}
          </RoomyGrid>
        </Form>
      )}
    </Formik>
  );
};
